import { Box, CircularProgress } from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';
import { useAxios } from '@/hooks/useAxios';
import { b64toBlob } from '../../../utils/b64toBlob';
import { FileUploader } from '../FileUploader';
import { withS3Bucket } from '../services/s3bucket';

export interface IProps {
  convertTo: string;
  acceptedFiletypes: string[];
  btnLabel: string;
  btnSize?: 'small' | 'medium' | 'large';
  btnVariant?: 'text' | 'contained' | 'outlined';
  onSuccess?: (files: any[]) => unknown;
  upload: any;
}

type ConvertedFile = {
  FileName: string;
  FileExt: string;
  FileSize: number;
  FileData: string;
};

type UploadResponse = {
  FileExt: string;
  FileId: string;
  FileName: string;
  Url: string;
};

type ConversionResult = {
  ConversionCost: number;
  Files: ConvertedFile[];
};

function dataHasFileId(data: any): data is UploadResponse {
  return 'FileId' in data;
}

function dataHasConversionResult(data: any): data is ConversionResult {
  return 'ConversionCost' in data && 'Files' in data;
}

const FileConverter = withS3Bucket()(
  ({
    acceptedFiletypes,
    btnLabel,
    btnSize,
    btnVariant,
    convertTo,
    onSuccess: emit,
    upload,
  }: IProps) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const [enabled, setEnabled] = useState<boolean>(true);

    const [transfered, setTransfered] = useState<any[]>([]);

    const [queue, setQueue] = useState(0);

    const [label, setLabel] = useState<string>(btnLabel);

    const { data, error, send, reset } = useAxios();

    const handleClose = () => {
      reset();
      setEnabled(true);
    };

    const handleOpen = () => {
      reset();
      setEnabled(false);
    };

    const resetUploader = () => {
      setEnabled(true);
      setLabel(btnLabel);

      if (inputRef.current instanceof HTMLInputElement) {
        inputRef.current.value = '';
        inputRef.current.files = null;
      }
    };

    const handleUpload = (formData?: FormData) => {
      setEnabled(false);
      setLabel('Uploading...');

      send({
        method: 'post',
        url: `https://v2.convertapi.com/upload`,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    };

    const handleConversion = (data: UploadResponse) => {
      setLabel('Converting...');

      send({
        url: '/api/visitor/converter/transform',
        params: {
          from: data.FileExt,
          to: convertTo,
          file: data.FileId,
        },
      });
    };

    const recursiveUpload = async (files: ConversionResult['Files']) => {
      setQueue(files.length);

      for (const file of files) {
        await upload(
          new File(
            [b64toBlob(file.FileData, file.FileExt)],
            `${file.FileName}.${file.FileExt}`
          ),
          (process: object) => {
            setTransfered((processed: any[]) => [...processed, process]);
          }
        );
      }
    };

    useEffect(() => {
      if (error !== undefined) {
        resetUploader();
        return;
      }

      if (data && dataHasConversionResult(data)) {
        recursiveUpload(data.Files);
        reset();
      }

      if (data && dataHasFileId(data)) {
        handleConversion(data);
        reset();
      }

      return;
    }, [error, data]);

    useEffect(() => {
      if (transfered.length > 0 && transfered.length === queue) {
        resetUploader();

        if (typeof emit === 'function') {
          emit(transfered);
        }

        return;
      }

      return;
    }, [transfered]);

    return (
      <FileUploader
        accept={acceptedFiletypes}
        disabled={!enabled}
        errorMessage={error ? (error as any).response.data.message : error}
        icon="transform"
        label={label}
        maxUpload={157286400} // 150MB
        onClose={handleClose}
        onOpen={handleOpen}
        onSelect={handleUpload}
        ref={inputRef}
        size={btnSize}
        variant={btnVariant}
      >
        {!enabled && (
          <Box pl={1}>
            <CircularProgress disableShrink size={14} />
            {transfered.length > 0 && (
              <span style={{ marginLeft: '4px' }}>
                {transfered.length}/{queue}
              </span>
            )}
          </Box>
        )}
      </FileUploader>
    );
  }
);

export { FileConverter };
